import { useState } from 'react';
import { Alert, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ClaudeCodeLoginGate from '../../components/claude-code/ClaudeCodeLoginGate';
import DauChart from '../../components/claude-code/DauChart';
import EmployeeTokenRanking from '../../components/claude-code/EmployeeTokenRanking';
import AgentCostGovernancePanel from './AgentCostGovernancePanel';
import AgentRolloutHealthPanel from './AgentRolloutHealthPanel';
import TopAgentsTable from '../../components/claude-code/TopAgentsTable';
import TopMcpToolsTable from '../../components/claude-code/TopMcpToolsTable';
import TopSkillsTable from '../../components/claude-code/TopSkillsTable';
import { useDashboardOverview } from '../../apps/apis/claudeCodeDashboard/hooks';
import { ROUTES } from '../../routes/routes';
import { HELP_TEXT } from '../../components/claude-code/agentTelemetryHelp';
import { HelpLabel } from '../../components/claude-code/HelpTooltip';

const WINDOW_OPTIONS = [7, 14, 30, 60, 90];

const SECTION_DESCRIPTIONS: Record<string, string> = {
  skills: HELP_TEXT.topSkills,
  agents: HELP_TEXT.topAgents,
  'mcp-tools': HELP_TEXT.topMcpTools,
  employees: HELP_TEXT.tokenUsageByEmployee,
};

const SectionTitle = ({ title, topKind }: { title: string; topKind: string }) => (
  <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
    <Card.Title className="h6 mb-0">
      <HelpLabel description={SECTION_DESCRIPTIONS[topKind] ?? 'Agent telemetry section.'}>{title}</HelpLabel>
    </Card.Title>
    <Link to={ROUTES.agentTelemetry.topList(topKind)} className="small">
      View all
    </Link>
  </div>
);

const ClaudeCodeOverviewPage = () => {
  const [windowDays, setWindowDays] = useState(30);
  return (
    <ClaudeCodeLoginGate>
      {(session) => (
        <Container fluid className="py-4">
          <div className="d-flex align-items-end justify-content-between mb-3 flex-wrap gap-2">
            <div>
              <h2 className="mb-1">
                <HelpLabel description="Aggregate Agent Plugin telemetry overview. Raw employee prompts and outputs are not stored or displayed.">
                  Agent Telemetry — Overview
                </HelpLabel>
              </h2>
              <div className="text-muted small">
                Signed in as <strong>{session.email}</strong>
                {session.isAdmin ? <span className="ms-2 badge bg-primary">admin</span> : null}
              </div>
            </div>
            <Form.Group controlId="windowDays" className="d-flex align-items-center gap-2">
              <Form.Label className="mb-0 small text-muted">
                <HelpLabel description={HELP_TEXT.window}>Window</HelpLabel>
              </Form.Label>
              <Form.Select
                size="sm"
                value={windowDays}
                onChange={(e) => setWindowDays(Number(e.target.value))}
                style={{ width: 120 }}
              >
                {WINDOW_OPTIONS.map((days) => (
                  <option key={days} value={days}>
                    {days} days
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Link to={ROUTES.agentTelemetry.proposals} className="btn btn-outline-primary btn-sm">
              {session.isAdmin ? 'Skill proposals' : 'My skill proposals'}
            </Link>
            {session.isAdmin ? (
              <Link to={ROUTES.agentTelemetry.roleAssets} className="btn btn-outline-secondary btn-sm">
                Role asset library
              </Link>
            ) : null}
            {session.isAdmin ? (
              <Link to={ROUTES.agentTelemetry.sharedAssetHealth} className="btn btn-outline-secondary btn-sm">
                Shared asset health
              </Link>
            ) : null}
          </div>
          {session.isAdmin ? (
            <OverviewBody windowDays={windowDays} />
          ) : (
            <Alert variant="warning">Overview is admin-only. Visit your employee page from the URL bar.</Alert>
          )}
        </Container>
      )}
    </ClaudeCodeLoginGate>
  );
};

function OverviewBody({ windowDays }: { windowDays: number }) {
  const { data, isLoading, error } = useDashboardOverview(windowDays);
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }
  if (error) {
    return <Alert variant="danger">Failed to load overview: {(error as Error).message}</Alert>;
  }
  if (!data) {
    return null;
  }
  return (
    <>
      <AgentRolloutHealthPanel windowDays={windowDays} />
      <AgentCostGovernancePanel windowDays={windowDays} />
      <Card className="mb-3">
        <Card.Body>
          <Card.Title className="h6">
            <HelpLabel description={HELP_TEXT.dailyActiveEmployees}>Daily active employees</HelpLabel>
          </Card.Title>
          <DauChart data={data.dailyActiveEmployees} />
        </Card.Body>
      </Card>
      <Row className="g-3">
        <Col md={6}>
          <Card>
            <Card.Body>
              <SectionTitle title="Top skills" topKind="skills" />
              <TopSkillsTable data={data.topSkills} />
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Body>
              <SectionTitle title="Top agents" topKind="agents" />
              <TopAgentsTable data={data.topAgents} />
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Body>
              <SectionTitle title="Top MCP tools" topKind="mcp-tools" />
              <TopMcpToolsTable data={data.topMcpTools} />
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Body>
              <SectionTitle title="Token usage by employee" topKind="employees" />
              <EmployeeTokenRanking data={data.tokenUsageByEmployee} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default ClaudeCodeOverviewPage;
